import {
    AccessTime,
    DeviceThermostat,
    MailOutline,
    SupervisedUserCircleOutlined,
    SwapCalls,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { Fragment } from 'react';
import { Campaign, CampaignChannelLabels, Send, SplitActions, SplitTypes } from 'domains/campaigns/types';
import CustomCampaignNavigationButton from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationButton';
import { channelIcons } from '../../../utils';

type Props = {
    campaign: Campaign;
    isPreviewOpen: boolean;
};

// Navigation component for A/B test campaigns
const ABTestNavigation = ({ campaign }: { campaign: Campaign }) => {
    const sendsCount = campaign.sends?.length ?? 0;
    const hasRemainingSend = sendsCount >= 3;

    return (
        <>
            <CustomCampaignNavigationButton icon={<SupervisedUserCircleOutlined />} label="audience" />
            <CustomCampaignNavigationButton icon={<DeviceThermostat />} label="A/B test" />
            <CustomCampaignNavigationButton icon={<MailOutline />} label="email A" />
            <CustomCampaignNavigationButton icon={<MailOutline />} label="email B" />
            {hasRemainingSend && <CustomCampaignNavigationButton icon={<MailOutline />} label="remaining" />}
        </>
    );
};

// Navigation component for regular (non-A/B test) campaigns
const RegularCampaignNavigation = ({ campaign }: { campaign: Campaign }) => {
    const renderSend = (send: Send) => {
        const { channel } = send;
        const Icon = channelIcons[channel];
        const label = send?.splitAction === SplitActions.EXIT ? 'exit' : CampaignChannelLabels[channel];

        return <CustomCampaignNavigationButton icon={<Icon />} label={label} />;
    };

    const renderSendDetails = (send: Send, index: number) => {
        // The first send cannot be time delayed and we do not need to indicate that there's a time
        // delay on the second action in a split. The delay is inicated before the split details.
        const isTimeDelayedSend = index !== 0 && send?.splitType !== SplitTypes.DOES_NOT_MEET;
        // Presently, all splits begin with a meets criteria action. As such, we want to show
        // the conditional split details before that send.
        const shouldShowConditionalSplitDetails = send?.splitType === SplitTypes.MEETS;

        return (
            <Fragment key={`send-${index}`}>
                {isTimeDelayedSend && <CustomCampaignNavigationButton icon={<AccessTime />} label="time delay" />}
                {shouldShowConditionalSplitDetails && (
                    <CustomCampaignNavigationButton icon={<SwapCalls />} label="conditional split" />
                )}
                {send?.splitType && <div>{send.splitType}</div>}
                {renderSend(send)}
            </Fragment>
        );
    };

    return (
        <>
            <CustomCampaignNavigationButton icon={<SupervisedUserCircleOutlined />} label="audience" />
            {campaign.sends?.map((send, index) => renderSendDetails(send, index))}
        </>
    );
};

const CustomCampaignDetailsNavigation = ({ campaign, isPreviewOpen }: Props) => {
    const isABTest = Boolean(campaign.abTestConfig);

    return (
        <Box display={isPreviewOpen ? 'none' : 'block'} ml={3} height="100%" width="75%">
            {isABTest ? <ABTestNavigation campaign={campaign} /> : <RegularCampaignNavigation campaign={campaign} />}
        </Box>
    );
};

export default CustomCampaignDetailsNavigation;
